// Ship-type categories, colours, nav-status labels and the vessel marker icon.

export const CATEGORIES = [
  { key: "tanker", label: "Tanker", hex: "#DC2626" },
  { key: "cargo", label: "Cargo", hex: "#D97706" },
  { key: "fishing", label: "Fishing", hex: "#0891B2" },
  { key: "passenger", label: "Passenger", hex: "#7C3AED" },
  { key: "tug_service", label: "Tug / Service", hex: "#059669" },
  { key: "other_unknown", label: "Other / Unknown", hex: "#334155" },
];

export const CATEGORY_HEX = CATEGORIES.reduce((a, c) => ((a[c.key] = c.hex), a), {});
export const STALE_HEX = "#94A3B8";

export const INFRA_HEX = { oil: "#B45309", unknown: "#64748B", wind: "#0369A1" };
export function infraLabel(label) {
  return { oil: "Oil Platform", unknown: "Unknown Structure", wind: "Wind Turbine" }[label] || "Structure";
}

// Colored (non-SDF) markers. We pre-tint per category/label and select via
// `icon-image` so MapLibre never uses the SDF `icon-color` placement path
// (which can crash intermittently in symbol placement).
function _canvas(size) {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  return [c, c.getContext("2d")];
}

export function makeArrowImage(hex) {
  const s = 28;
  const [c, ctx] = _canvas(s);
  ctx.clearRect(0, 0, s, s);
  ctx.fillStyle = hex;
  ctx.strokeStyle = "rgba(2,6,23,0.55)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(14, 3);
  ctx.lineTo(23, 25);
  ctx.lineTo(14, 20);
  ctx.lineTo(5, 25);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  return ctx.getImageData(0, 0, s, s);
}

export function makePlatformImage(hex) {
  const s = 26;
  const [c, ctx] = _canvas(s);
  ctx.clearRect(0, 0, s, s);
  ctx.fillStyle = hex;
  ctx.strokeStyle = "rgba(2,6,23,0.6)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(13, 3);
  ctx.lineTo(23, 13);
  ctx.lineTo(13, 23);
  ctx.lineTo(3, 13);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  return ctx.getImageData(0, 0, s, s);
}

// Registers all pre-colored marker images on the map (call once, after style load).
export function registerMarkerImages(map) {
  CATEGORIES.forEach((c) => {
    if (!map.hasImage(`v-${c.key}`)) map.addImage(`v-${c.key}`, makeArrowImage(c.hex));
  });
  if (!map.hasImage("v-stale")) map.addImage("v-stale", makeArrowImage(STALE_HEX));
  Object.entries(INFRA_HEX).forEach(([label, hex]) => {
    if (!map.hasImage(`rig-${label}`)) map.addImage(`rig-${label}`, makePlatformImage(hex));
  });
}

export function vesselIcon(v) {
  if (v.stale) return "v-stale";
  return CATEGORY_HEX[v.category] ? `v-${v.category}` : "v-other_unknown";
}

export function categoryLabel(key) {
  return (CATEGORIES.find((c) => c.key === key) || CATEGORIES[5]).label;
}

const NAV_STATUS = {
  0: "Under way (engine)",
  1: "At anchor",
  2: "Not under command",
  3: "Restricted manoeuvrability",
  4: "Constrained by draught",
  5: "Moored",
  6: "Aground",
  7: "Engaged in fishing",
  8: "Under way sailing",
  15: "Undefined",
};

export function navStatusText(code) {
  if (code === null || code === undefined) return "—";
  return NAV_STATUS[code] || `Status ${code}`;
}

export function fmtUTC(ms) {
  if (!ms && ms !== 0) return "—";
  return new Date(ms).toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

// Disputed island groups in the South China Sea — approximate outlines of the
// archipelago extents (scattered reefs/islets cannot be drawn individually) with
// a representative label position for each.
export const SEA_REGIONS = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: { name: "Paracel Islands" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [111.05, 16.55], [111.3, 17.15], [112.2, 17.05], [112.95, 16.75],
          [113.05, 16.1], [112.4, 15.65], [111.5, 15.75], [111.0, 16.1], [111.05, 16.55],
        ]],
      },
    },
    {
      type: "Feature",
      properties: { name: "Spratly Islands" },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [111.3, 11.4], [113.5, 11.3], [115.2, 10.2], [116.9, 8.4],
          [115.8, 5.2], [113.5, 3.9], [111.2, 4.4], [109.8, 6.8],
          [110.4, 9.5], [111.3, 11.4],
        ]],
      },
    },
  ],
};

export const SEA_REGION_CENTROIDS = {
  "Paracel Islands": [111.9, 16.4],
  "Spratly Islands": [113.2, 7.8],
};

// Undersea cable infrastructure — single unified colour (violet/indigo).
export const CABLE_HEX = "#6366F1";

export function haversineKm(lon1, lat1, lon2, lat2) {
  const R = 6371, r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r, dLon = (lon2 - lon1) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function segKm(plon, plat, alon, alat, blon, blat) {
  const r = Math.PI / 180, kLat = 111.32, kLon = 111.32 * Math.cos(plat * r);
  const px = plon * kLon, py = plat * kLat;
  const ax = alon * kLon, ay = alat * kLat, bx = blon * kLon, by = blat * kLat;
  const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// Approximate shortest distance (km) from a point to a cable LineString/MultiLineString.
export function distancePointToLineKm(lon, lat, geometry) {
  if (!geometry) return Infinity;
  const segs = geometry.type === "MultiLineString" ? geometry.coordinates : [geometry.coordinates];
  let best = Infinity;
  for (const line of segs) {
    for (let i = 0; i < line.length - 1; i++) {
      const d = segKm(lon, lat, line[i][0], line[i][1], line[i + 1][0], line[i + 1][1]);
      if (d < best) best = d;
    }
  }
  return best;
}
